import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BicycleIcon, CalendarBlankIcon, MapPinIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { destinationCoverImage } from "@/lib/destinationCover";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const localIsoDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const normalizedOrigin = (value) => (
  value && Number.isFinite(value.lat) && Number.isFinite(value.lng)
    ? { label: value.label || value.name || translateLabel("Starting point"), lat: value.lat, lng: value.lng }
    : null
);

export function PlanThisRideButton({
  destination,
  initialDate = "",
  initialOrigin = null,
  source = "park_detail",
  compact = false,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => initialDate || localIsoDate());
  const [origin, setOrigin] = useState(() => normalizedOrigin(initialOrigin));
  const [originQuery, setOriginQuery] = useState(() => normalizedOrigin(initialOrigin)?.label || "");
  const [profileChecked, setProfileChecked] = useState(() => Boolean(normalizedOrigin(initialOrigin)));
  const [originBusy, setOriginBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const returnTo = useMemo(
    () => `${location.pathname}${location.search || ""}`,
    [location.pathname, location.search],
  );

  useEffect(() => {
    if (!open) return;
    if (initialDate) setDate(initialDate);
    const handedOffOrigin = normalizedOrigin(initialOrigin);
    if (handedOffOrigin) {
      setOrigin(handedOffOrigin);
      setOriginQuery(handedOffOrigin.label);
      setProfileChecked(true);
    }
  }, [open, initialDate, initialOrigin]);

  useEffect(() => {
    if (!open || !user || profileChecked || origin) return;
    let active = true;
    api.get("/rider-profile")
      .then(({ data }) => {
        if (!active) return;
        const home = data?.home_location;
        if (home && Number.isFinite(home.lat) && Number.isFinite(home.lng)) {
          const next = { label: home.name || translateLabel("Home location"), lat: home.lat, lng: home.lng };
          setOrigin(next);
          setOriginQuery(next.label);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) setProfileChecked(true);
      });
    return () => { active = false; };
  }, [open, user, profileChecked, origin]);

  const start = () => {
    if (!user) {
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      return;
    }
    setError("");
    setOpen(true);
  };

  const resolveOrigin = async () => {
    const query = originQuery.trim();
    if (!query) return;
    setOriginBusy(true);
    setError("");
    try {
      const { data } = await api.get("/geocode", { params: { q: query } });
      const first = data?.results?.[0];
      if (!first) {
        setOrigin(null);
        setError(translateLabel("No matching starting point found."));
        return;
      }
      setOrigin({ label: first.name || query, lat: first.lat, lng: first.lng });
      setOriginQuery(first.name || query);
    } catch (requestError) {
      setOrigin(null);
      setError(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setOriginBusy(false);
    }
  };

  const createPlan = async () => {
    if (!origin) {
      setError(translateLabel("Choose your starting point first."));
      return;
    }
    if (!date) {
      setError(translateLabel("Choose a ride date."));
      return;
    }

    setCreating(true);
    setError("");
    try {
      let contextItem = null;
      try {
        const contextResponse = await api.get("/compare-context", {
          params: {
            destination_ids: [destination.id],
            lat: origin.lat,
            lng: origin.lng,
            date,
          },
        });
        contextItem = contextResponse.data?.items?.[0] || null;
      } catch {
        // The plan can still be created; route/weather remain unknown.
      }

      const response = await api.post("/ride-plans", {
        date,
        origin,
        max_drive_minutes: contextItem?.drive_minutes ?? 0,
        wants: [],
        candidates: [{
          destination_id: destination.id,
          name: destination.name,
          city: destination.city || null,
          country: destination.country || null,
          lat: destination.lat ?? null,
          lng: destination.lng ?? null,
          drive_minutes: contextItem?.drive_minutes ?? null,
          drive_km: contextItem?.drive_km ?? null,
          weather_condition: contextItem?.weather?.condition ?? null,
          weather_temp_min_c: contextItem?.weather?.temp_min_c ?? null,
          weather_temp_max_c: contextItem?.weather?.temp_max_c ?? null,
          precipitation_probability_pct: contextItem?.weather?.precipitation_probability_pct ?? null,
          day_ticket_price: destination.day_ticket_price ?? null,
          image: destinationCoverImage(destination),
        }],
        selected_destination_id: destination.id,
      });

      trackEvent("ride_plan_created", {
        source,
        candidate_count: 1,
        park_id: String(destination.id),
      });
      setOpen(false);
      navigate(`/ride-plan/${response.data.room.plan.share_token}`);
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={start}
          data-testid="plan-this-ride"
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#CCFF00]/25 bg-[#CCFF00]/[0.08] px-2.5 py-1.5 text-[11px] font-extrabold text-[#CCFF00] transition-colors hover:bg-[#CCFF00]/[0.13]"
        >
          <BicycleIcon size={13} weight="bold" /> {translateLabel("Plan this ride")}
        </button>
      ) : (
        <button
          type="button"
          onClick={start}
          data-testid="plan-this-ride"
          className="flex w-full items-center justify-between gap-3 rounded-2xl border border-[#CCFF00]/30 bg-[#CCFF00]/[0.09] px-4 py-3.5 text-left transition-colors hover:bg-[#CCFF00]/[0.13]"
        >
          <span className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#CCFF00] text-black">
              <BicycleIcon size={21} weight="bold" />
            </span>
            <span className="min-w-0">
              <span className="block text-[10px] font-black uppercase tracking-[0.16em] text-[#CCFF00]">{translateLabel("Ride selected")}</span>
              <span className="mt-0.5 block text-sm font-extrabold text-white">{translateLabel("Plan this ride")}</span>
              <span className="mt-0.5 block text-[11px] text-zinc-400">{translateLabel("Pick a date, then invite your friends.")}</span>
            </span>
          </span>
          <span className="shrink-0 text-lg font-bold text-[#CCFF00]">→</span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="z-[1450] w-[calc(100%-1.5rem)] max-w-lg rounded-3xl border-white/10 bg-[#121214] p-5 text-white sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black uppercase">{translateLabel("Plan this ride")}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {translateLabel("{name} is already chosen. Set the day and where you are starting from.", { name: destination.name })}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <label className="block">
              <span className="mb-1.5 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.15em] text-zinc-500">
                <CalendarBlankIcon size={13} /> {translateLabel("Ride date")}
              </span>
              <input
                type="date"
                min={localIsoDate()}
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className="w-full rounded-xl border border-white/10 bg-black/25 px-3 py-2.5 text-sm text-white outline-none focus:border-[#CCFF00]/40"
                data-testid="plan-this-ride-date"
              />
            </label>

            <div>
              <span className="mb-1.5 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.15em] text-zinc-500">
                <MapPinIcon size={13} /> {translateLabel("Starting point")}
              </span>
              <div className="flex gap-2">
                <input
                  value={originQuery}
                  onChange={(event) => {
                    setOriginQuery(event.target.value);
                    setOrigin(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      resolveOrigin();
                    }
                  }}
                  placeholder={translateLabel("City or postcode")}
                  className="min-w-0 flex-1 rounded-xl border border-white/10 bg-black/25 px-3 py-2.5 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-[#CCFF00]/40"
                  data-testid="plan-this-ride-origin"
                />
                <button
                  type="button"
                  onClick={resolveOrigin}
                  disabled={originBusy || !originQuery.trim()}
                  className="rounded-xl border border-white/10 px-3.5 py-2.5 text-xs font-bold text-zinc-200 hover:bg-white/[0.05] disabled:opacity-40"
                >
                  {translateLabel(originBusy ? "Finding…" : "Set")}
                </button>
              </div>
              {origin && (
                <p className="mt-1.5 text-[11px] font-semibold text-emerald-300">
                  {translateLabel("Starting from {place}", { place: translateLabel(origin.label) })}
                </p>
              )}
            </div>

            {error && (
              <p className="rounded-xl border border-red-400/20 bg-red-400/10 px-3 py-2.5 text-xs font-semibold text-red-200" data-testid="plan-this-ride-error">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={createPlan}
              disabled={creating || !origin || !date}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#CCFF00] px-4 py-3 text-sm font-extrabold text-black transition-colors hover:bg-[#B8E600] disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="create-single-ride-plan"
            >
              {creating && <SpinnerGapIcon size={17} className="animate-spin" />}
              {translateLabel(creating ? "Creating ride…" : "Create ride plan")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
